// Every user-facing line of the browser wallet (the store's errors and the dialog's fixed copy). Plain "what happened
// + what to do"; never a server text, a zod path or an exception message. No en or em dashes.

/** MoonHub.API's MAX_BACKUPS_PER_USER (a spam bound, not a product limit). */
export const MAX_BACKUPS = 100;

export const RESTORE_COPY = {
    wrongSecret: 'The password and recovery key did not open this backup.',
    wrongPassword: 'That password did not open this backup.',
    badRecoveryKey:
        "That recovery key doesn't look right. It has 32 letters and digits, shown in 8 groups of 4.",
    needsRecoveryKey: 'This backup needs its recovery key.',
    badFormat: "This backup can't be read. Open the Snappy app to check it.",
    failed: "Couldn't restore right now. Please try again.",
    addressMismatch: 'Backup holds another wallet than the one shown.',
} as const;

/** The backup password checklist (`PasswordChecklist`): one row per check of the engine's `checkBackupPassword`. */
export const CHECKLIST_COPY = {
    minLength: (need: number) => `At least ${need} characters`,
    lowercase: 'A lowercase letter',
    uppercase: 'An uppercase letter',
    digit: 'A number',
    symbol: 'A symbol',
    words: 'At least 5 different words',
    notCommon: 'Not a common password or pattern',
    // Under the words row while it is unmet: why a typed word did not count (never the words themselves).
    ignoredWords: (n: number) =>
        n === 1
            ? '1 word does not count: a word needs at least 3 characters and cannot be one character repeated.'
            : `${n} words do not count: a word needs at least 3 characters and cannot be one character repeated.`,
    duplicateWords: (n: number) =>
        n === 1
            ? '1 repeated word counts once.'
            : `${n} repeated words count once.`,
    hintComplex:
        'Or type a passphrase: 5 or more different words, 25+ characters, no symbols needed.',
    hintPassphrase:
        'Passphrase mode: 5 or more different words, 25+ characters.',
    progressLabel: 'Password requirements met',
    met: 'done',
    unmet: 'not yet',
    failed: 'not met',
} as const;

/** The backup form's two schemes: a password and a recovery key (the default), or a strong password only. */
export const SCHEME_COPY = {
    passwordOnly: 'Do not use a recovery key, only a strong password',
    recoveryKey: 'Use a recovery key instead',
    warning:
        'We cannot reset this password or recovery key, or recover your wallet. If you lose this browser and either of them, your funds are gone.',
    warningPasswordOnly:
        'Without a recovery key, this password is all that protects your backup. We cannot reset or recover it. If you lose it, your funds are gone.',
} as const;

/** Shown while scrypt runs (the restore's decrypt, the backup's encrypt and upload). */
export const RESTORE_PROGRESS_COPY = 'Decrypting your backup…';
export const SAVE_PROGRESS_COPY = 'Encrypting and saving your backup…';

export const BLOB_COPY = {
    expired: 'Your sign-in expired. Sign in again to restore.',
    noBackup: 'No backup found for this account.',
    rateLimited: 'Too many attempts. Wait a minute and try again.',
    failed: "Couldn't load your backup. Please try again.",
    unreadable: "This backup can't be read. Open the Snappy app to check it.",
} as const;

export const SETUP_COPY = {
    cap: `This account already holds ${MAX_BACKUPS} backups. Delete one first.`,
    failed: "Couldn't set up the wallet right now. Please try again.",
    invalidPhrase:
        'That is not a valid recovery phrase. Check the words and their order.',
    otherWallet: 'That recovery phrase belongs to another wallet.',
} as const;

export const SAVE_COPY = {
    readBack:
        'The backup was saved but could not be read back from Snappy. Try again.',
    expired: {
        create: 'Your sign-in expired before the backup was saved. Sign in again and create the wallet once more; the words you wrote down are not needed.',
        import: 'Your sign-in expired before the backup was saved. Sign in again and import your recovery phrase once more.',
        backup: 'Your sign-in expired before the backup was saved. Sign in again and back up this wallet once more.',
    },
    limit: `This account already holds ${MAX_BACKUPS} backups. Delete one first.`,
    retry: "Couldn't save the backup. Please try again.",
    tooLarge: 'The backup was larger than Snappy accepts. Try again.',
    invalid:
        "Snappy refused the backup. Check that this device's clock is correct and try again.",
    failed: "Couldn't save the backup right now. Please try again.",
} as const;

export const VOUCHER_COPY = {
    needed: 'A launch code is needed first.',
    invalid: "That code isn't valid. Check it and try again.",
    disabled: 'This launch code is no longer active.',
    expired: 'This launch code has expired.',
    exhausted: 'This launch code has reached its maximum uses.',
    rateLimited: 'Too many attempts. Wait a few minutes and try again.',
    failed: "Couldn't check the code right now. Please try again.",
} as const;

/** factions' voucher error codes (the onboarding queue's and the admission redeem's). */
export const VOUCHER_ERROR_COPY: Readonly<Record<string, string>> = {
    voucherInvalid: VOUCHER_COPY.invalid,
    voucherDisabled: VOUCHER_COPY.disabled,
    voucherExpired: VOUCHER_COPY.expired,
    voucherExhausted: VOUCHER_COPY.exhausted,
    rateLimited: VOUCHER_COPY.rateLimited,
    RATE_LIMITED: VOUCHER_COPY.rateLimited,
};

export const DELETE_COPY = {
    failed: "Couldn't delete the backup. Please try again.",
} as const;

/** The protect step (plan 2026-10-06 §3.2): how the key at rest is encrypted. */
export const PROTECT_COPY = {
    passkeyOption: 'Use a passkey',
    passkeyHint:
        'Face ID, Touch ID, Windows Hello or a security key. Recommended.',
    pinOption: 'Use a 6-digit PIN',
    pinInstead: 'Use a PIN instead',
    passkeyInstead: 'Use a passkey instead',
    passkeyUnavailable: "Passkeys aren't available in this browser.",
    tryPasskeyAgain: 'Try passkey again',
    waitingForPasskey: 'Waiting for your passkey…',
    pinLabel: 'PIN',
    pinConfirmLabel: 'Type the PIN again',
    pinMismatch: 'The PINs do not match.',
    pinFormat: 'Enter 6 digits.',
    continue: 'Continue',
    sealing: 'Securing your wallet…',
    cancel: 'Cancel',
    failed: "Couldn't protect the wallet. Please try again.",
    forget: 'Forget this wallet on this browser',
    forgetConfirm:
        'Forget this wallet on this browser? You can restore it from your backup or its recovery phrase.',
    forgetConfirmAction: 'Forget it',
    keep: 'Keep it',
} as const;

/** Plain copy for a failed passkey step (the error's name decides; never its text). */
export const PASSKEY_ERROR_COPY = {
    cancelled: 'The passkey prompt was cancelled.',
    unsupported:
        "This passkey can't protect a wallet here. Use a PIN, or try another passkey or browser.",
    failed: "Couldn't use the passkey.",
} as const;

/** The unlock view (plan 2026-10-06 §3.3). */
export const UNLOCK_COPY = {
    withPasskey: 'Unlock with passkey',
    unlock: 'Unlock',
    unlocking: 'Unlocking…',
    pinLabel: 'PIN',
    wrongPin: 'Wrong PIN.',
    passkeyUnsupported:
        "This passkey can't unlock the wallet here. Try the browser or device you set it up with, or forget this wallet on this browser and restore it from your backup.",
    unreadable:
        "This wallet can't be unlocked here. Forget this wallet on this browser and restore it from your backup.",
    failed: "Couldn't unlock the wallet. Please try again.",
    forgot: 'Forgot your PIN or lost your passkey? Forget this wallet on this browser and restore it from your backup.',
    notNow: 'Not now',
    retryIn: (seconds: number) =>
        `Too many wrong PINs. Try again in ${seconds} s.`,
    confirmPin: 'Enter your current PIN.',
    currentPinLabel: 'Current PIN',
    confirmPasskey: 'Confirm with your passkey.',
    confirm: 'Confirm',
    confirmWithPasskey: 'Confirm with passkey',
    cancel: 'Cancel',
    signPin:
        'Enter your PIN to approve this. You will not be asked again in this tab.',
    signPasskey:
        'Confirm with your passkey to approve this. You will not be asked again in this tab.',
} as const;

/** The "Wallet lock" section of the backups view. */
export const LOCK_SETTINGS_COPY = {
    title: 'Wallet lock',
    pin: 'Unlocked with a 6-digit PIN',
    passkey: 'Unlocked with a passkey',
    sealed: 'Locked on this browser. Unlock it to trade.',
    sessionUnlocked:
        'Unlocked for this session. Sending funds or changing wallet settings asks for your PIN or passkey once per tab.',
    lockAll: 'Lock on all tabs',
    lockAllHint:
        'Locks your Snappy Wallet in every open tab. You unlock it again with your PIN or passkey.',
    unlock: 'Unlock',
    changePin: 'Change PIN',
    switchToPasskey: 'Switch to passkey',
    switchToPin: 'Switch to PIN',
    passkeyNote:
        'A passkey stays in your passkey manager after a switch or a forget. Delete it there if you no longer need it.',
} as const;

export const TOAST_COPY = {
    restored: 'Snappy Wallet restored',
    protected: 'Snappy Wallet protected',
    lockChanged: 'Wallet lock updated',
    lockedEverywhere: 'Snappy Wallet locked on all tabs',
    created: 'Snappy Wallet created',
    imported: 'Snappy Wallet imported',
    backedUp: 'Backup saved',
    backedUpDetail: 'Backed up with Snappy.',
    deleted: 'Backup deleted',
} as const;

/** Dialog titles per view. */
export const DIALOG_TITLES = {
    backups: 'Your Snappy Wallet',
    restore: 'Restore your Snappy Wallet',
    voucher: 'Launch access',
    terms: 'Before you start',
    replaceHeld: 'Replace the wallet on this browser?',
    createBefore: 'Create your wallet',
    createWords: 'Your recovery phrase',
    import: 'Import a recovery phrase',
    backup: 'Back up your wallet',
    done: 'You are all set',
    protect: 'Protect your wallet',
    unlock: 'Unlock your Snappy Wallet',
    confirm: "Confirm it's you",
} as const;

export const DIALOG_DESCRIPTIONS = {
    restore:
        "Enter the backup password you chose when you backed up this wallet, and its recovery key if it has one. Snappy can't reset or recover either.",
    voucher:
        'Snappy Wallet is in limited launch. Enter your launch code to back up your wallet.',
    replaceHeld: (short: string) =>
        `Snappy Wallet ${short} will be forgotten here. Make sure it is backed up or you have its recovery phrase.`,
    createBefore:
        'A new wallet is created in this browser. You will see its 12 word recovery phrase next, so have a pen and paper ready.',
    createWords:
        'Write these 12 words down in order and keep them somewhere safe and offline. Do not screenshot them. Anyone who has them can take your funds.',
    import: 'Enter your 12 or 24 word recovery phrase, words separated by spaces. It is kept only in this browser.',
    backup: 'Your recovery phrase is encrypted in this browser with a password you choose, and a recovery key only you hold unless you turn it off. Snappy stores only the encrypted copy and cannot read, recover or reset it.',
    done: 'Your Snappy Wallet is ready.',
    protect:
        'Your Snappy Wallet key stays on this browser, encrypted. Choose how you unlock it.',
    protectLegacy:
        'Your Snappy Wallet key is kept on this browser without protection. Choose how you unlock it and it is encrypted right away.',
    unlock: (short: string) =>
        `Snappy Wallet ${short} is locked on this browser.`,
} as const;

export const DISCARD_COPY = {
    create: 'Discard this new wallet? It has no backup yet, so it cannot be recovered.',
    import: 'Stop the import? Nothing was saved. You can import the recovery phrase again later.',
    backup: 'Stop the backup? Nothing was saved. This browser keeps its wallet.',
} as const;

export const BANNER_COPY = {
    expired: 'Your session ended. Sign in again.',
    manageBackups: 'Sign in to manage backups.',
    restore: 'Restore your Snappy Wallet to trade.',
    setUp: 'Set up your Snappy Wallet to trade.',
    sealed: 'Unlock your Snappy Wallet',
    protect: 'Protect your Snappy Wallet to trade.',
    unfunded: 'Deposit USDC to start.',
    activationRequired: 'Approve trading once.',
} as const;

export const BANNER_ACTIONS = {
    signIn: 'Sign in',
    restore: 'Restore',
    setUp: 'Set up',
    unlock: 'Unlock',
    protect: 'Protect',
    deposit: 'Deposit',
    approve: 'Approve',
} as const;
